import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { moment } from '@seiue/moment'
import { Button, Modal, Radio, Toast, DatePicker } from '@seiue/ui'
import { compact, isArray, safeParseJSON } from '@seiue/util'
import { Form } from 'antd'
import React, { FC, useEffect, useRef } from 'react'

import {
  MethodEnum,
  Notification,
  NotificationReq,
  ReflectionTypeEnum,
  UserForegroundDomainEnum,
  notificationApi,
} from 'packages/sdks-next/growp'

import { ContentCard } from '@/components/ContentCard'
import { ModalLayout } from '@/components/ModalLayout'
import { NoticeEditor } from '@/components/NoticeEditor'
import { setSelectedReceiverIds as setSelectedIds } from '@/components/NoticeEditor/data'
import {
  NotificationAttachment,
  attachment,
  receivers,
  setAttachment,
  setVariables,
  setReceivers,
  setReflectionDomain,
  variables,
} from '@/components/NoticeEditor/variablesMenuConfig'
import { reflectionTypeToDomain } from '@/components/NotificationItem/utils'
import { WaringText } from '@/components/Styled'
import { TaskHeaderLeft } from '@/components/TaskHeaderLeft'

import { SelectMember } from './SelectMember'
import { RichTextEditorRef } from 'packages/components/RichText/Editor/types'

/**
 * 编辑通知弹窗
 *
 * @param param0 - 参数
 * @param param0.notification - 通知
 * @param param0.onClose - 关闭弹窗
 * @param param0.activityId - 活动 ID
 * @param param0.reflectionType - 人员类型
 * @returns 组件
 */
export const EditNotificationModal: FC<{
  notification?: Notification
  onClose: () => void
  activityId: number
  reflectionType?: ReflectionTypeEnum
}> = ({ notification, onClose, reflectionType, activityId }) => {
  const isEdit = !!notification

  const richtextRef = useRef<RichTextEditorRef>(null)

  const [selectedRowKeys, setSelectedRowKeys] = React.useState<number[]>(
    notification?.rids || []
  )
  const [currentStep, setCurrentStep] = React.useState<number>(0)
  const [validField, setValidField] = React.useState<boolean>(false)
  const [isDirty, setIsDirty] = React.useState<boolean>(false)
  const [publishedAt, setPublishedAt] = React.useState<string | undefined>(
    notification?.method === MethodEnum.Draft
      ? undefined
      : notification?.publishedAt || moment().format('YYYY-MM-DD HH:mm:00')
  )

  const [form] = Form.useForm<{ title: string; content: string }>()

  const [method, setMethod] = React.useState<MethodEnum>(
    notification?.method || MethodEnum.Now
  )

  // 获取用户信息，变量
  const { data: fetchReceivers, loading: loadingReceiver } =
    notificationApi.useQueryNotificationReflections(
      {
        id: notification?.id || 0,
        query: {
          paginated: 0,
        },
      },
      {
        disable: !notification?.id,
      }
    )

  const { api: createNotification } = notificationApi.useCreateNotification({
    reload: [notificationApi.queryNotifications],
  })

  const { api: updateNotification } = notificationApi.useUpdateNotification({
    reload: [notificationApi.queryNotifications],
  })

  const onSelectedRowKeysChange = (selectedKeys: React.Key[]) => {
    setIsDirty(true)
    setSelectedRowKeys(selectedKeys as number[])
  }

  useEffect(() => {
    setSelectedIds(selectedRowKeys)
  }, [selectedRowKeys])

  useEffect(() => {
    const variablesColumns =
      (notification?.setting as { variables?: string[] } | undefined)
        ?.variables || []

    setVariables(variablesColumns)

    setAttachment(
      // 后端对 object 类型的数据默认值给了个 []
      isArray(notification?.attachment) && !notification?.attachment.length
        ? undefined
        : ((notification?.attachment || undefined) as
            | NotificationAttachment
            | undefined)
    )
    setReceivers(
      compact(
        fetchReceivers?.map((r) =>
          !r.reflection?.id
            ? undefined
            : {
                rid: r.reflection?.id,
                id: r.id,
                variables: r.variables,
                schoolId: r.schoolId,
              }
        )
      )
    )
  }, [notification, fetchReceivers])

  const close = () => {
    setVariables([])
    setAttachment(undefined)
    setSelectedIds([])
    setReceivers([])
    setReflectionDomain(UserForegroundDomainEnum.Informal)
    onClose()
  }

  return (
    <ModalLayout
      content={
        <>
          <ContentCard style={{ minHeight: 200 }}>
            {currentStep === 0 ? (
              <SelectMember
                activityId={activityId}
                reflectionType={reflectionType}
                selectedRowKeys={selectedRowKeys}
                onSelectedRowKeysChange={onSelectedRowKeysChange}
              />
            ) : (
              <NoticeEditor
                form={form}
                initialValues={{
                  title: notification?.title,
                  content: notification?.content,
                }}
                onValuesChange={() => {
                  setIsDirty(true)
                }}
                ref={richtextRef}
              />
            )}
          </ContentCard>
          <ContentCard style={{ textAlign: 'center' }}>
            {currentStep === 1 ? (
              <>
                <Form.Item
                  label={'发送时间'}
                  required={true}
                  style={{ textAlign: 'left', marginBottom: 20 }}
                >
                  <Radio.Group
                    defaultValue={MethodEnum.Now}
                    options={[
                      { label: '立即发送', value: MethodEnum.Now },
                      { label: '定时发送', value: MethodEnum.Future },
                      { label: '暂不发送', value: MethodEnum.Draft },
                    ]}
                    value={method}
                    onChange={(e) => {
                      setIsDirty(true)
                      setMethod(e.target.value)
                      setValidField(false)
                      if (e.target.value === 'now') {
                        setPublishedAt(moment().format('YYYY-MM-DD HH:mm:00'))
                      } else {
                        setPublishedAt(undefined)
                      }
                    }}
                  />
                </Form.Item>
                {method === MethodEnum.Future ? (
                  <Form.Item style={{ textAlign: 'left', marginLeft: 88 }}>
                    <DatePicker
                      style={{ width: 320 }}
                      showTime={true}
                      onChange={(_, dateString) => {
                        setIsDirty(true)
                        setPublishedAt(
                          moment(dateString).format('YYYY-MM-DD HH:mm:00')
                        )
                        setValidField(false)
                      }}
                      value={publishedAt ? moment(publishedAt) : undefined}
                    />
                    {validField && !publishedAt ? (
                      <WaringText>{'请选择定时发送时间'}</WaringText>
                    ) : null}
                  </Form.Item>
                ) : null}
              </>
            ) : null}
            <Button
              onClick={() => {
                if (currentStep === 0 && !selectedRowKeys.length) {
                  Toast.warning('请先选择成员')
                  return
                }
                setCurrentStep(currentStep === 0 ? 1 : 0)
              }}
              iconRight={currentStep === 0 ? faArrowRight : undefined}
              icon={currentStep === 1 ? faArrowLeft : undefined}
            >
              {currentStep === 0 ? '下一步' : '上一步'}
            </Button>
          </ContentCard>
        </>
      }
      title={
        <TaskHeaderLeft
          title={isEdit ? '编辑通知' : '新建通知'}
          onClose={() => {
            if (isDirty) {
              Modal.confirm({
                title: '确定离开该页面吗？',
                content:
                  '正在离开该页面，离开后修改数据将被清空；确认要离开吗？',
                onOk: () => {
                  close()
                },
              })
            } else {
              close()
            }
          }}
        />
      }
      stepsParams={{
        current: currentStep,
        items: [{ title: '选择成员' }, { title: '撰写通知' }],
        onChange: () => {
          if (currentStep === 0 && !selectedRowKeys.length) {
            Toast.warning('请先选择成员')
            return
          }
          setCurrentStep(currentStep === 0 ? 1 : 0)
        },
      }}
      headerRight={
        currentStep === 0 ? null : (
          <Button
            type="primary"
            onClick={() => {
              setValidField(true)
              form.validateFields().then(async (values) => {
                if (method === MethodEnum.Future && !publishedAt) {
                  return
                }

                if (reflectionType) {
                  // 纯文本
                  const plainText = richtextRef.current?.getPlainText?.()

                  const receiversRes = selectedRowKeys.map((rid) => {
                    const existReceive = receivers?.find((r) => r.rid === rid)
                    if (existReceive) {
                      return { rid, variables: existReceive.variables }
                    }
                    return {
                      rid,
                      variables: '',
                    }
                  })

                  const payload: NotificationReq = {
                    method,
                    title: values.title,
                    content: values.content,
                    publishedAt,
                    reflectionDomain: reflectionTypeToDomain(reflectionType),
                    withdrawReason: '',
                    setting: {
                      plainText,
                      variables,
                    },
                    receivers: receiversRes,
                    attachment,
                    activityId,
                  }

                  if (isEdit) {
                    await updateNotification({
                      id: notification?.id,
                      payload,
                    })

                    Toast.success('更新成功')
                    close()
                  } else {
                    await createNotification(payload)

                    Toast.success('发布成功')
                    close()
                  }
                }
              })
            }}
          >
            {isEdit ? '更新' : '发送'}
          </Button>
        )
      }
    />
  )
}
